import React, { lazy, Suspense, useEffect, useMemo, useState } from 'react';

import { useColorMode, useRampTheme } from '@ramp/components';
import { createBrowserRouter, RouterProvider, useLocation, useNavigate, useRouteError } from 'react-router-dom';
import { useAuthStore } from 'store/auth';
import { useLayoutStore } from 'store/layout';
import { Role } from 'types';
import { QueryParamProvider } from 'use-query-params';
import { ReactRouter6Adapter } from 'use-query-params/adapters/react-router-6';
import { hasRole, setFavicon } from 'utils/_utils/session';

import MobileUnsupportedModal from 'components/MobileUnsupportedModal';
import PageLoader from 'components/PageLoader';
import { isMdmAndroidCallbackPath, isMdmEnrollPath } from './routes';

// Each role's routes load on demand, so the shell and the enroll page stay small.
const AdminRoutes = lazy(() => import('./admin/AdminRoutes'));
const BaseRoutes = lazy(() => import('./base/BaseRoutes'));
const FirmAdminRoutes = lazy(() => import('./firmadmin/FirmAdminRoutes'));
const SuperAdminRoutes = lazy(() => import('./superadmin/SuperAdminRoutes'));
const UserRoutes = lazy(() => import('./user/UserRoutes'));
const MdmEnroll = lazy(() => import('pages/base/MdmEnroll'));
const MdmAndroidCallback = lazy(() => import('pages/base/MdmAndroidCallback'));

const CurrentRoutes = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const shouldLogout = useMemo(() => {
    const loggedAsAdmin = !!useAuthStore.getState().loginAsAdmin.clientEntityId;
    const loggedAsPartner =
      !!useAuthStore.getState().loginAsPartnerAdmin.partnerEntityId ||
      hasRole(Role.firmadmin, useAuthStore.getState()?.user?.role);

    if (location.pathname.startsWith('/partneradmin') && loggedAsPartner && loggedAsAdmin) {
      useAuthStore.getState().logoutUserAsAdmin();
      return true;
    }

    return false;
  }, [location]);

  useEffect(() => {
    if (shouldLogout) {
      navigate(location, { replace: true });
    }
  }, [shouldLogout, navigate, location]);

  const [user, loginAsAdmin, loginAsPartnerAdmin] = useAuthStore(store => [
    store.user,
    store.loginAsAdmin,
    store.loginAsPartnerAdmin
  ]);

  // Standalone MDM enrollment page: render at a fixed path in BOTH authed and
  // unauthed states. Placed after all hooks (Rules of Hooks) and before the
  // role branching so it takes precedence regardless of the signed-in role.
  if (isMdmEnrollPath(location.pathname)) return <MdmEnroll />;

  // Google's return address after the Android enterprise binding. Fixed path for
  // the same reason, and it renders regardless of role.
  if (isMdmAndroidCallbackPath(location.pathname)) return <MdmAndroidCallback />;

  // If user is logged in & has user role -> UserRouter
  if (hasRole(Role.user, user?.role)) return <UserRoutes />;

  // If user is logged in & has admin role -> AdminRouter
  if (hasRole(Role.admin, user?.role)) return <AdminRoutes />;

  // If user is logged in & has firmadmin role -> FirmAdminRouter
  if (hasRole(Role.firmadmin, user?.role)) {
    if (!loginAsAdmin.clientEntityId || shouldLogout) return <FirmAdminRoutes />;
    return <AdminRoutes />;
  }

  // If user is logged in & has superadmin role -> SuperAdminRouter
  if (hasRole(Role.superadmin, user?.role)) {
    if (loginAsAdmin.clientEntityId && !shouldLogout) return <AdminRoutes />;
    if (loginAsPartnerAdmin.partnerEntityId) return <FirmAdminRoutes />;

    return <SuperAdminRoutes />;
  }

  // If user is not logged in -> BaseRouter
  return <BaseRoutes />;
};

// Every route stays in the descendant `<Routes>` of its role. The data router around them lets a page
// block leaving with `useBlocker` (the compliance editor's unsaved changes).
const AppShell = () => (
  <>
    {/* Inside the router so it can react to route changes via useLocation. */}
    <MobileUnsupportedModal />
    <QueryParamProvider adapter={ReactRouter6Adapter}>
      {/* Not AppLoader: its mount re-runs login(), which resets an entered partner or client context. */}
      <Suspense fallback={<PageLoader />}>
        <CurrentRoutes />
      </Suspense>
    </QueryParamProvider>
  </>
);

// A data router catches a render error in its own boundary. Throw it on, so the Sentry error boundary in
// `App` reports it and shows "Something went wrong", as it did with `<BrowserRouter>`.
const RethrowRouteError = () => {
  throw useRouteError();
};

const Router: React.FC = () => {
  const { setBrand } = useRampTheme();
  const { colorMode, setColorMode } = useColorMode();
  const theme = useLayoutStore(store => store.theme);
  // Made once per mount, not at import, so loading the module starts no history listener.
  const [router] = useState(() =>
    createBrowserRouter([{ path: '*', element: <AppShell />, errorElement: <RethrowRouteError /> }])
  );

  useEffect(() => {
    setFavicon(colorMode === 'dark' ? theme.logo_secondary_dark : theme.logo_secondary_light);
    setBrand?.({
      light: theme.color_scheme_light,
      dark: theme.color_scheme_dark
    });

    if (theme.theme === 'all') return;
    if (theme.theme === colorMode) return;

    setColorMode(theme.theme);
  }, [theme]);

  useEffect(() => {
    setFavicon(colorMode === 'dark' ? theme.logo_secondary_dark : theme.logo_secondary_light);
  }, [colorMode, theme]);

  return <RouterProvider router={router} />;
};

export default Router;
